// Importing React and external libs
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// Importing d3 libraries
import * as d3 from 'd3';
import * as fc from 'd3fc';

// Importing helix files
import useGenomePositions from 'helix-hooks/chart/useGenomePositions';
import { BLOCK_COLORS, DISEASE_REGION_COLOR } from 'helix-styles/colors';

// Importing component style
import './style.scss';

const positionsToBlocks = (chromLength, colors) => (definition, offset) => {
  if (!definition) return [];

  const { initial_color: initial, positions } = definition;
  const colorChangePositions = Array.isArray(positions) ? positions : [];
  const blockPositions = [0, ...colorChangePositions, chromLength];

  const blocks = [];
  for (let index = 0; index < blockPositions.length - 1; index++) {
    let colorIndex = 0;
    if (initial > 0) {
      const offset = index % 2;
      colorIndex = initial % 2 === 0 ? initial - offset : initial + offset;
    }
    blocks.push({
      color: colors[colorIndex],
      end: blockPositions[index + 1],
      offset,
      start: blockPositions[index],
    });
  }
  return blocks;
};

const Karyomap = ({
  assembly,
  bandwidth,
  blockColors,
  blockData,
  flanking,
  geneRegions,
  geneRegionColor,
  height,
  id,
  useDevicePixelRatio,
  visibleRegion,
  visibleRegionColor,
}) => {
  const chartRef = useRef();
  const { chroms, chromPositions, isLoading } = useGenomePositions(assembly);
  const maxLength = chromPositions['1']?.end ?? 0;
  const [chartWidth, setChartWidth] = useState(null);
  const [chartHeight, setChartHeight] = useState(null);

  const blocks = useMemo(() => {
    if (isLoading) return [];

    const blocksFromData = [];
    for (const [chromStr, { maternal, paternal }] of Object.entries(blockData)) {
      const chrom = chromStr.replace('chr', '');
      const chromLength = chromPositions[chrom].length;

      const getBlocksFromPositions = positionsToBlocks(chromLength, blockColors);
      const paternalBlocks = getBlocksFromPositions(paternal, -1);
      const maternalBlocks = getBlocksFromPositions(maternal, 1);

      [...paternalBlocks, ...maternalBlocks].forEach(
        block => blocksFromData.push({ ...block, chrom: block.offset < 0 ? `p-${chrom}` : `m-${chrom}` })
      );
    }
    return blocksFromData;
  }, [chromPositions, blockData, isLoading]);

  useEffect(() => {
    if (!(blocks.length > 0 && chartRef.current)) return;

    const xScale = d3.scalePoint().domain(chroms.reduce((prev, chrom) => [...prev, `gl-${chrom}`, `p-${chrom}`, `m-${chrom}`, `gr-${chrom}`], []));
    const yScale = d3.scaleLinear().domain([maxLength, 0]);

    const barSeries = fc
      .seriesCanvasBar()
      .bandwidth(bandwidth)
      .crossValue((d) => d.chrom)
      .baseValue((d) => d.start)
      .mainValue((d) => d.end)
      .decorate((context, d) => {
        context.fillStyle = d.color;
      });

    const chart = fc
      .chartCartesian({
        xScale,
        yScale,
        xAxis: {
          bottom: (scale) => fc.axisBottom(d3.scaleBand().domain(chroms).range(scale.range()))
        }
      })
      .useDevicePixelRatio(useDevicePixelRatio)
      .yOrient('left')
      .yTickFormat(tick => `${tick / 1e6}MB`)
      .xPadding(0.5)
      .canvasPlotArea(barSeries)
      .svgPlotArea(fc.seriesSvgBar());

    d3.select(chartRef.current).datum(blocks).call(chart);

    d3.select('d3fc-svg').on('measure', (event) => {
      setChartWidth(event.detail.width);
      setChartHeight(event.detail.height);
    });

  }, [bandwidth, blocks, chroms, maxLength]);

  useEffect(() => {
    if (chartHeight > 0 && chartWidth > 0) {
      const xScaleBand = d3.scaleBand().domain(chroms).range([0, chartWidth]);
      const yScale = d3.scaleLinear().domain([maxLength, 0]).range([chartHeight, 0]);

      const regions = [];

      if (visibleRegion) {
        regions.push({ ...visibleRegion, fill: 'none', stroke: visibleRegionColor });
      }

      if (geneRegions.length > 0) {
        geneRegions.forEach(geneRegion => {
          const { chrom, start, end } = geneRegion;
          const length = end - start;
          const minSize = flanking * 1e6;
          const diff = Math.max((minSize - length) / 2, 0);

          regions.push({
            chrom,
            end: end + diff,
            fill: geneRegionColor,
            start: start - diff,
            stroke: 'none',
          });
        });
      }

      d3.select('.svg-plot-area svg')
        .selectAll('rect')
        .data(regions)
        .join('rect')
        .attr('fill', d => d.fill)
        .attr('fill-opacity', '0.4')
        .attr('stroke', d => d.stroke)
        .attr('stroke-width', '2.5px')
        .attr('width', xScaleBand.bandwidth())
        .attr('height', d => yScale(d.end) - yScale(d.start))
        .attr('x', d => xScaleBand(`${d.chrom}`))
        .attr('y', d => yScale(d.start));
    }
  }, [
    blocks,
    chartHeight,
    chartWidth,
    chroms,
    geneRegions,
    geneRegionColor,
    maxLength,
    visibleRegion,
    visibleRegionColor
  ]);

  return (
    <section className='karyomap' id={id} ref={chartRef} style={{ height }} />
  );
};

const blockDefinition = PropTypes.shape({
  initial_color: PropTypes.number,
  positions: PropTypes.arrayOf(PropTypes.number),
});

const regionShapeDefinition = PropTypes.shape({
  chrom: PropTypes.string.isRequired,
  end: PropTypes.number.isRequired,
  start: PropTypes.number.isRequired,
});

Karyomap.propTypes = {
  assembly: PropTypes.oneOf(['hg19', 'hg38']).isRequired,
  bandwidth: PropTypes.number,
  blockColors: PropTypes.arrayOf(PropTypes.string).isRequired,
  blockData: PropTypes.objectOf(
    PropTypes.shape({
      maternal: blockDefinition,
      paternal: blockDefinition
    })
  ).isRequired,
  flanking: PropTypes.number,
  geneRegions: PropTypes.arrayOf(regionShapeDefinition),
  geneRegionColor: PropTypes.string.isRequired,
  height: PropTypes.string.isRequired,
  id: PropTypes.string,
  useDevicePixelRatio: PropTypes.bool.isRequired,
  visibleRegion: regionShapeDefinition,
  visibleRegionColor: PropTypes.string.isRequired
};

Karyomap.defaultProps = {
  assembly: 'hg38',
  bandwidth: 10,
  blockColors: BLOCK_COLORS.map(color => color.fill),
  height: '420px',
  flanking: 2,
  geneRegions: [],
  geneRegionColor: DISEASE_REGION_COLOR.fill,
  useDevicePixelRatio: true,
  visibleRegionColor: '#2f528f',
};

export default Karyomap;
